import {
	AddCircleOutlined,
	AutoFixHighOutlined,
	CallSplitOutlined,
	CloudSyncOutlined,
	DeleteOutlined,
	EventNoteOutlined,
	InfoOutlined,
	LinkOutlined,
	MedicalServicesOutlined,
	ScheduleOutlined as ScheduleOutlinedIcon,
	StorefrontOutlined,
} from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	FormControl,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { HourListField } from "../../../../components/UIToolkit/Inputs/HourListField";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import { ReadOnlyMetadataField } from "../../../../components/UIToolkit/molecules/ReadOnlyMetadataField";
import { SettingRow } from "../../../../components/UIToolkit/molecules/SettingRow";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import {
	BORDERLESS_INPUT_SX,
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_BODY_ROW_SX,
	CONFIG_TABLE_CONTAINER_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
} from "../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { useAuth } from "../../../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { NUMERIC_FIELD_WIDTH, STATUS } from "../../../../theme/tokens";
import { agencySourceLabel } from "../../../../utils/agencyIntegrations";
import { DEFAULT_AGENCY_POST_DELAY_HOURS, MIN_AGENCY_POST_DELAY_HOURS } from "../../../../utils/agencyPostDelay";
import { AgencySource, type EmrIntegrationInput, EmrType, ExternalSource, type HrisIntegrationType } from "../../../../utils/models";
import { CensusRecalculationSetting } from "./CensusRecalculationSetting";
import { ClipboardSetupWizard } from "./clipboard/ClipboardSetupWizard";
import { EMR_FIELD_LABELS, EMR_TYPES, EMR_UUID_FIELD, MAX_PCC_FACILITY_ID, UUID_REGEX } from "./generalMetadataConsts";
import { AddHrisSourceDialog } from "./integrations/AddHrisSourceDialog";
import { AgencyIntegrationStatusChip, AgencyIntegrationToggle, useAgencyToggleState } from "./integrations/AgencyIntegrationToggle";
import { HrisSourceCard, type IndexedIntegration } from "./integrations/HrisSourceCard";
import { NursaFacilityIdField } from "./integrations/NursaFacilityIdField";
import { NursaReadinessChips } from "./integrations/NursaReadinessChips";
import { SIDE_LAID_GROUND_SX } from "./ratesConsts";
import { SyncedJobsAside } from "./SyncedJobsAside";
import { useHrisSourceCapabilities } from "./sftpHooks";
import type { GeneralFormData } from "./types";

// EMR types whose census is ADT-driven — the bed-hold setting only means something for them (Yardi has no ADT census).
const BEDHOLD_CAPABLE_EMR_TYPES = [EmrType.PCC, EmrType.Matrix, EmrType.Matrix360, EmrType.ReliableHealth];

const EmrIntegrationRow: FC<{
	integration: EmrIntegrationInput;
	idx: number;
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
}> = ({ integration, idx, formData, setFormData }) => {
	const { t } = useTranslation();
	const usedTypes = (formData.emrIntegrations ?? []).map((e) => e.type);
	const labels = EMR_FIELD_LABELS[integration.type];
	const hasOrgId = labels.orgId !== undefined;
	// Which id field holds a UUID varies per vendor (PCC = orgId, Yardi = facilityId); Matrix 360's
	// orgId is a Facility OID, not a UUID, so it's excluded here (validated separately on save).
	const uuidField = EMR_UUID_FIELD[integration.type];

	const update = (patch: Partial<EmrIntegrationInput>) => {
		const updated = [...formData.emrIntegrations];
		updated[idx] = { ...updated[idx], ...patch };
		setFormData((prev) => ({ ...prev, emrIntegrations: updated }));
	};

	// A UUID field is malformed when non-empty and failing the UUID regex → show a warning adornment.
	const malformedUuidAdornment = (field: "facilityId" | "orgId") =>
		uuidField === field && integration[field] && !UUID_REGEX.test(integration[field]) ? (
			<InputAdornment position="start" data-testid={`emr-integration-uuid-invalid-${idx}`}>
				<Tooltip title={t("department.config.integrations.emr.invalidUuid")} placement="top">
					<InfoOutlined sx={{ fontSize: 16, color: STATUS.WARNING.main, cursor: "default" }} />
				</Tooltip>
			</InputAdornment>
		) : undefined;

	// Only show types not already used in other rows (plus the current row's own type).
	const availableTypes = EMR_TYPES.filter(({ value }) => value === integration.type || !usedTypes.includes(value));

	return (
		<TableRow sx={CONFIG_TABLE_BODY_ROW_SX} data-testid={`emr-integration-row-${idx}`}>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { width: 220 }]}>
				<FormControl size="small" fullWidth sx={BORDERLESS_INPUT_SX}>
					<Select
						data-testid={`emr-integration-type-select-${idx}`}
						value={integration.type}
						onChange={(e) =>
							// Clear field values when switching type since field semantics differ.
							update({ type: e.target.value as EmrType, facilityId: undefined, orgId: undefined })
						}
					>
						{availableTypes.map(({ value, label }) => (
							<MenuItem key={value} value={value} data-testid={`emr-integration-type-option-${value}`}>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { width: 220 }]}>
				<TextField
					size="small"
					fullWidth
					sx={BORDERLESS_INPUT_SX}
					type={integration.type === EmrType.PCC ? "number" : "text"}
					value={integration.facilityId ?? ""}
					onChange={(e) => update({ facilityId: (uuidField === "facilityId" ? e.target.value.toLowerCase() : e.target.value) || undefined })}
					placeholder={labels.facilityId}
					slotProps={{
						htmlInput: { "data-testid": `emr-integration-primary-id-${idx}` },
						input:
							integration.type === EmrType.PCC && integration.facilityId && Number(integration.facilityId) > MAX_PCC_FACILITY_ID
								? {
										startAdornment: (
											<InputAdornment position="start">
												<Tooltip title={t("department.config.integrations.emr.invalidFacilityId")} placement="top">
													<InfoOutlined sx={{ fontSize: 16, color: STATUS.WARNING.main, cursor: "default" }} />
												</Tooltip>
											</InputAdornment>
										),
									}
								: { startAdornment: malformedUuidAdornment("facilityId") },
					}}
				/>
			</TableCell>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				{hasOrgId ? (
					<TextField
						size="small"
						fullWidth
						sx={BORDERLESS_INPUT_SX}
						value={integration.orgId ?? ""}
						onChange={(e) => update({ orgId: (uuidField === "orgId" ? e.target.value.toLowerCase() : e.target.value) || undefined })}
						placeholder={integration.type === EmrType.Matrix360 ? t("department.config.emr.matrix360FacilityOid") : labels.orgId}
						slotProps={{
							htmlInput: { "data-testid": `emr-integration-uuid-${idx}` },
							input: { startAdornment: malformedUuidAdornment("orgId") },
						}}
					/>
				) : (
					<Box sx={{ color: "text.disabled", px: 1 }} data-testid={`emr-integration-uuid-dash-${idx}`}>
						—
					</Box>
				)}
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { verticalAlign: "middle" }]}>
				<SystemSwitch checked={integration.active} onChange={(e) => update({ active: e.target.checked })} dataTestId={`emr-integration-active-${idx}`} />
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { verticalAlign: "middle" }]}>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					leadingIcon={<DeleteOutlined fontSize="small" />}
					tooltip={t("department.config.integrations.delete")}
					aria-label={t("department.config.integrations.delete")}
					data-testid={`emr-integration-delete-${idx}`}
					onClick={() => {
						const updated = formData.emrIntegrations.filter((_, i) => i !== idx);
						setFormData((prev) => ({ ...prev, emrIntegrations: updated }));
					}}
				/>
			</TableCell>
		</TableRow>
	);
};

export const IntegrationsTabContent: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	/**
	 * The NON-dirty-marking form setter. Used only for values a server-side action already committed —
	 * the Clipboard wizard writes `clipboard_facility_id` itself, and marking the form dirty for a value
	 * the server already holds would make the next Save re-send it (and a discard look like it undid a
	 * committed change).
	 */
	syncFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isDirty: boolean;
	/** An unsaved department has no persisted id, so every support wizard here is hidden for it. */
	isNewDept: boolean;
	/** Fires the Nursa webhook subscription for the department's persisted facility id. */
	onRegisterNursaWebhook: () => void;
	isRegisteringNursaWebhook: boolean;
	onOpenHrisSummary: (summaryType: HrisIntegrationType) => void;
}> = ({ formData, setFormData, syncFormData, isDirty, isNewDept, onRegisterNursaWebhook, isRegisteringNursaWebhook, onOpenHrisSummary }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const isSupport = !!userInfo?.support;
	const { chosenDepartment, refreshDepartmentData } = useHospitalData();
	const [, setSearchParams] = useSearchParams();
	const [clipboardWizardOpen, setClipboardWizardOpen] = useState(false);
	const [addSourceOpen, setAddSourceOpen] = useState(false);
	// `isLoading`, not `isPending`: a disabled query (non-support) stays pending forever, and that must
	// not read as "still loading".
	const { data: sourceCapabilities, isLoading: isLoadingSourceCapabilities, error: sourceCapabilitiesError } = useHrisSourceCapabilities(isSupport);
	// The column stores "combine"; schedulers think in terms of keeping the two halves of a split day
	// apart. Derived once so the label, the switch and the write can't drift out of sync.
	const separateClipboardScheduled = !formData.combineConsecutiveAgencyShifts;
	const delayedAgencyPostingEnabled = useFeatureFlag(FEATURE_FLAGS.DelayedAgencyPostingEnabled);
	// Deliberately `=== "inactive"`, not `!== "active"`. An UNMAPPED Clipboard department still syncs
	// (see `_select_account`), so treating "never mapped" as off would disable settings that are live.
	const nursaInactive = useAgencyToggleState(AgencySource.Nursa) === "inactive";
	const clipboardInactive = useAgencyToggleState(AgencySource.Clipboard) === "inactive";

	// Deliberately NOT defaulted to an empty Map on failure: absent must stay absent. Collapsing "we
	// don't know" into `false` told the operator to contact engineering about a source they can
	// configure right here, and removed the File column from it.
	const capabilityBySource = useMemo(
		() => (sourceCapabilities ? new Map(sourceCapabilities.map((capability) => [capability.source, capability])) : undefined),
		[sourceCapabilities],
	);
	// The Add-source dialog only needs the file/API split.
	const fileBasedBySource = useMemo(
		() => (capabilityBySource ? new Map([...capabilityBySource].map(([source, capability]) => [source, capability.file_based])) : undefined),
		[capabilityBySource],
	);

	// DAT-773: one card per SOURCE, each holding that source's feeds. Grouped rather than stored that
	// way because the `hris` table is still one row per feed — the card owns the fields that belong to
	// the source and writes them onto every row it covers. Insertion order is preserved so adding a
	// source appends its card at the bottom instead of reshuffling the list.
	const sourceGroups = useMemo(() => {
		const groups = new Map<ExternalSource, IndexedIntegration[]>();
		(formData.hrisIntegrations ?? []).forEach((integration, index) => {
			// Clipboard's rows are owned by the actuals toggle and the wizard below, and `clipboard` is
			// deliberately absent from HRIS_SOURCES, so a card for it would render with a blank source.
			// Hidden from the DISPLAY only — the rows stay in the payload, because
			// `upsert_hris_integrations` is delete-all-then-reinsert and dropping them from the save
			// would silently turn actuals off.
			if (integration.source === ExternalSource.Clipboard) return;
			const existing = groups.get(integration.source);
			if (existing) existing.push({ integration, index });
			else groups.set(integration.source, [{ integration, index }]);
		});
		return [...groups.entries()];
	}, [formData.hrisIntegrations]);

	const addHrisSource = useCallback(
		(source: ExternalSource) => {
			setAddSourceOpen(false);
			setFormData((prev) => ({
				...prev,
				hrisIntegrations: [...prev.hrisIntegrations, { source, type: "employees" as HrisIntegrationType, hris_department_id: "", active: true }],
			}));
		},
		[setFormData],
	);

	// The actuals switch: an ACTIVE (clipboard, shifts) row. It rides the normal dirty → Save flow
	// rather than getting its own endpoint — the config save rewrites the whole array, so a dedicated
	// endpoint would write a row the next Save silently deletes.
	const clipboardShiftsRow = (formData.hrisIntegrations ?? []).find(
		(integration) => integration.source === ExternalSource.Clipboard && integration.type === "shifts",
	);
	const syncActualShifts = !!clipboardShiftsRow?.active;
	// DEFAULT: the column is optional on the department payload; absent means the delay isn't configured,
	// which is the same thing as zero days. Matches `AgencyShiftSettings`.
	const agencyScheduledWithoutPunchDays = chosenDepartment.agencyScheduledWithoutPunchDays ?? 0;

	const setSyncActualShifts = useCallback(
		(next: boolean) => {
			setFormData((prev) => {
				const rows = prev.hrisIntegrations ?? [];
				const idx = rows.findIndex((integration) => integration.source === ExternalSource.Clipboard && integration.type === "shifts");
				if (idx === -1) {
					// Nothing to turn off when the row was never created.
					if (!next) return prev;
					// `hris_department_id` is read by nothing for clipboard, but every production row carries
					// the department id — keep the convention so the table stays readable.
					return {
						...prev,
						hrisIntegrations: [
							...rows,
							{ source: ExternalSource.Clipboard, type: "shifts" as HrisIntegrationType, hris_department_id: chosenDepartment.department_id, active: true },
						],
					};
				}
				// Flip `active`, NEVER remove: the backend filters on it, and the row carries `alert_since`.
				const updated = [...rows];
				updated[idx] = { ...updated[idx], active: next };
				return { ...prev, hrisIntegrations: updated };
			});
		},
		[setFormData, chosenDepartment.department_id],
	);

	// Every reason the wizard cannot run, in the order the operator has to fix them. Each is a real
	// backend precondition, so saying which one is missing beats a generic "not available".
	const clipboardBlockers = useMemo<string[]>(() => {
		const blockers: string[] = [];
		if (!formData.jobIds?.length) blockers.push(t("department.config.clipboard.wizard.blocked.noJobs"));
		if (!formData.timezone?.trim()) blockers.push(t("department.config.clipboard.wizard.blocked.noTimezone"));
		if (agencyScheduledWithoutPunchDays > 0)
			blockers.push(t("department.config.clipboard.wizard.blocked.agencyWithoutPunch", { days: agencyScheduledWithoutPunchDays }));
		// Enrolling a facility into an integration nobody is listening to would produce a mapped,
		// silent department — the exact state this ticket exists to make visible.
		if (clipboardInactive) blockers.push(t("department.config.integrations.agency.blocked.inactive", { agency: agencySourceLabel(AgencySource.Clipboard) }));
		return blockers;
	}, [formData.jobIds, formData.timezone, agencyScheduledWithoutPunchDays, clipboardInactive, t]);

	const clipboardWizardDisabledReason = isDirty ? t("department.config.clipboard.wizard.blocked.dirty") : clipboardBlockers[0];

	const handleClipboardEnrolled = useCallback(
		(facilityId: string) => {
			// Non-dirtying: the server already holds this value. Then refresh department data, because the
			// post-to-agency gates read `chosenDepartment.clipboard_facility_id`, not form state.
			syncFormData((prev) => ({ ...prev, clipboardFacilityId: facilityId }));
			void refreshDepartmentData(chosenDepartment.department_id, false);
		},
		[syncFormData, refreshDepartmentData, chosenDepartment.department_id],
	);

	const handleOpenHrisMapping = useCallback(() => {
		setSearchParams(
			(prev) => {
				const params = new URLSearchParams(prev);
				params.set("tab", "hris-mapping");
				return params;
			},
			{ replace: true },
		);
	}, [setSearchParams]);

	const canAddEmr = (formData.emrIntegrations ?? []).length < EMR_TYPES.length;
	const hasBedholdCapableEmr = (formData.emrIntegrations ?? []).some((e) => BEDHOLD_CAPABLE_EMR_TYPES.includes(e.type));
	// Read off live form state (like hasBedholdCapableEmr) so deleting the PCC row hides the
	// recalculation control immediately, before Save — and adding one reveals it.
	const pccIntegration = (formData.emrIntegrations ?? []).find((e) => e.type === EmrType.PCC);
	const addEmrIntegration = useCallback(() => {
		setFormData((prev) => {
			const usedTypes = (prev.emrIntegrations ?? []).map((e) => e.type);
			const nextType = EMR_TYPES.find(({ value }) => !usedTypes.includes(value))?.value ?? EmrType.PCC;
			return { ...prev, emrIntegrations: [...(prev.emrIntegrations ?? []), { type: nextType, active: true }] };
		});
	}, [setFormData]);

	return (
		<Box component="div" sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
			<Box sx={SIDE_LAID_GROUND_SX}>
				{/* HRIS Integrations — editable form-grid with borderless in-cell inputs. The side column
				    carries the read-only Synced Jobs list, which answers "what does this sync?" next to
				    the sources doing the syncing (DAT-804); editing it opens the shared Manage-jobs dialog. */}
				<SettingsSection
					headerPlacement="side"
					icon={<CloudSyncOutlined />}
					title={t("department.config.integrations.section.hris")}
					description={t("department.config.integrations.section.hris.description")}
					action={<SyncedJobsAside formData={formData} setFormData={setFormData} isNewDept={isNewDept} />}
					layout="stack"
					contentMaxWidth="none"
					dataTestId="dept-config-integrations-hris-header"
				>
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
						{isSupport && (
							<>
								<SystemButton
									buttonVariant="secondary"
									size="small"
									leadingIcon={<CloudSyncOutlined fontSize="small" />}
									onClick={() => onOpenHrisSummary("employees")}
									data-testid="hris-employees-summary-button"
								>
									{t("integration.summary.employees.button")}
								</SystemButton>
								<SystemButton
									buttonVariant="secondary"
									size="small"
									leadingIcon={<ScheduleOutlinedIcon fontSize="small" />}
									onClick={() => onOpenHrisSummary("time_off")}
									data-testid="hris-time-off-summary-button"
								>
									{t("integration.summary.timeoff.button")}
								</SystemButton>
								<SystemButton
									buttonVariant="secondary"
									size="small"
									leadingIcon={<EventNoteOutlined fontSize="small" />}
									onClick={() => onOpenHrisSummary("shifts")}
									data-testid="hris-shifts-summary-button"
								>
									{t("integration.summary.shifts.button")}
								</SystemButton>
							</>
						)}
						<SystemButton
							buttonVariant="borderless"
							size="small"
							leadingIcon={<AddCircleOutlined fontSize="small" />}
							onClick={() => setAddSourceOpen(true)}
							data-testid="dept-config-integrations-hris-add"
						>
							{t("department.config.integrations.addIntegration")}
						</SystemButton>
					</Box>
					{/* Held until the capability answer lands. Rendering the cards early would assert
					    `fileBased=false` for every source — an "ask engineering" warning and a hidden File
					    column on sources that are in fact configurable right here. */}
					{isLoadingSourceCapabilities ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 5 }} role="status" aria-busy>
							<CircularProgress size={28} />
						</Box>
					) : sourceGroups.length === 0 ? (
						<EmptyState
							icon={<CloudSyncOutlined />}
							title={t("department.config.integrations.hris.empty.title")}
							hint={t("department.config.integrations.hris.empty.hint")}
							action={
								<SystemButton
									buttonVariant="secondary"
									size="small"
									leadingIcon={<AddCircleOutlined fontSize="small" />}
									onClick={() => setAddSourceOpen(true)}
									data-testid="dept-config-integrations-hris-empty-add"
								>
									{t("department.config.integrations.addIntegration")}
								</SystemButton>
							}
							dataTestId="dept-config-integrations-hris-empty"
						/>
					) : (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							{!!sourceCapabilitiesError && (
								<SystemBanner
									tone="warning"
									title={t("department.config.integrations.hris.capabilities.error.title")}
									description={t("department.config.integrations.hris.capabilities.error.description")}
									dataTestId="hris-capabilities-error"
								/>
							)}
							{sourceGroups.map(([source, rows]) => (
								<HrisSourceCard
									key={source}
									source={source}
									rows={rows}
									// undefined when the capability lookup failed — the card then withholds the
									// judgement instead of asserting "engineering only", and asks for both IDs
									// rather than hiding a field the source might need.
									capability={capabilityBySource?.get(source)}
									setFormData={setFormData}
									isDirty={isDirty}
									isSupport={isSupport}
								/>
							))}
						</Box>
					)}

					<AddHrisSourceDialog
						open={addSourceOpen}
						usedSources={sourceGroups.map(([source]) => source)}
						fileBasedBySource={fileBasedBySource}
						onClose={() => setAddSourceOpen(false)}
						onPick={addHrisSource}
					/>
				</SettingsSection>

				{/* EMR Integrations — editable form-grid with borderless in-cell inputs. */}
				<SettingsSection
					headerPlacement="side"
					icon={<MedicalServicesOutlined />}
					title={t("department.config.integrations.section.emr")}
					description={t("department.config.integrations.section.emr.description")}
					layout="stack"
					contentMaxWidth="none"
					dataTestId="dept-config-integrations-emr-header"
				>
					{canAddEmr && (
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<SystemButton
								buttonVariant="borderless"
								size="small"
								leadingIcon={<AddCircleOutlined fontSize="small" />}
								data-testid="emr-add-integration-button"
								onClick={addEmrIntegration}
							>
								{t("department.config.integrations.addIntegration")}
							</SystemButton>
						</Box>
					)}
					<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX} data-testid="emr-integrations-table">
						<Table sx={{ width: "100%" }} aria-label={t("department.config.integrations.section.emr")}>
							<TableHead>
								<TableRow>
									<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: 220 }]}>
										{t("department.config.integrations.emr.col.emr")}
									</TableCell>
									<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: 220 }]}>
										{t("department.config.integrations.emr.col.facilityId")}
									</TableCell>
									<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: 200 }]}>
										{t("department.config.integrations.emr.col.uuid")}
									</TableCell>
									<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: 80 }]}>
										{t("department.config.integrations.emr.col.active")}
									</TableCell>
									<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: 64 }]} />
								</TableRow>
							</TableHead>
							<TableBody data-testid="emr-integrations-table-body">
								{(formData.emrIntegrations ?? []).map((integration, idx) => (
									<EmrIntegrationRow key={`${integration.type}-${idx}`} integration={integration} idx={idx} formData={formData} setFormData={setFormData} />
								))}
								{(formData.emrIntegrations ?? []).length === 0 && (
									<TableRow>
										<TableCell colSpan={5} sx={{ borderBottom: "none", p: 0 }}>
											<EmptyState
												icon={<MedicalServicesOutlined />}
												title={t("department.config.integrations.emr.empty.title")}
												hint={t("department.config.integrations.emr.empty.hint")}
												action={
													<SystemButton
														buttonVariant="secondary"
														size="small"
														leadingIcon={<AddCircleOutlined fontSize="small" />}
														onClick={addEmrIntegration}
														data-testid="emr-empty-add"
													>
														{t("department.config.integrations.addIntegration")}
													</SystemButton>
												}
												dataTestId="emr-empty"
											/>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					{hasBedholdCapableEmr && (
						<SettingRow
							label={t("department.config.integrations.emr.countBedholdInCensus.label")}
							description={t("department.config.integrations.emr.countBedholdInCensus.description")}
							control={
								<SystemSwitch
									checked={formData.countBedholdInCensus}
									onChange={() => setFormData((prev) => ({ ...prev, countBedholdInCensus: !prev.countBedholdInCensus }))}
									dataTestId="emr-count-bedhold-in-census-toggle"
								/>
							}
							dataTestId="dept-config-integrations-emr-count-bedhold-in-census-row"
						/>
					)}
					{hasBedholdCapableEmr && (
						<SettingRow
							label={t("department.config.integrations.emr.censusIntervals.label")}
							description={t("department.config.integrations.emr.censusIntervals.description")}
							// Chips wrap and grow with the number of configured hours, so they get their own line
							// rather than competing with the label for the row's width.
							stacked
							control={
								<HourListField
									// Sorted for display only. The server sorts and dedups on write, and again on
									// read, so what is stored is canonical regardless of what this sends.
									value={[...(formData.censusIntervals ?? [])].sort((a, b) => a - b)}
									onChange={(next) =>
										// null, never [] — an empty array is rejected by the column CHECK, and means
										// "average over no readings" to the reader. Removing the last chip turns the
										// feature off.
										setFormData((prev) => ({ ...prev, censusIntervals: next.length > 0 ? next : null }))
									}
									pickerLabel={t("department.config.integrations.emr.censusIntervals.helper")}
									dataTestId="emr-census-intervals"
								/>
							}
							dataTestId="dept-config-integrations-emr-census-intervals-row"
						/>
					)}
					{/* Recalculating census from PCC only makes sense while PCC is one of the department's
					    EMR integrations — and only support can trigger it. */}
					{isSupport && pccIntegration && <CensusRecalculationSetting pccIntegration={pccIntegration} isDirty={isDirty} />}
				</SettingsSection>

				{/* Clipboard — how the agency's scheduled feed is turned into shifts. Rendered for every
				    department, not gated on `clipboard_facility_id`: an unmapped department whose Clipboard
				    login manages a single facility still syncs (see `_select_account`), so gating on the
				    mapping would hide a setting that is live for exactly those departments. */}
				<SettingsSection
					headerPlacement="side"
					icon={<CallSplitOutlined />}
					title={t("department.config.integrations.section.clipboard")}
					description={t("department.config.integrations.section.clipboard.description")}
					layout="stack"
					contentMaxWidth="none"
					action={
						// The status chip is rendered for EVERYONE, beside a wizard button only support sees:
						// "is Clipboard on for this department?" is a read, and the answer belongs in the header
						// where it is visible without scrolling into the section.
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
							{/* Ungated: a pure read off `/user_info`, calling no flagged endpoint. Gating it would let a
							    department be deactivated during rollout while its Integrations tab says nothing. */}
							<AgencyIntegrationStatusChip source={AgencySource.Clipboard} />
							{/* Hidden for a new department (the endpoints need a persisted id) and for non-support:
							    the wizard writes a shared AWS secret and opens posting to a live agency. */}
							{isSupport && !isNewDept && (
								<SystemButton
									buttonVariant="secondary"
									size="small"
									leadingIcon={<AutoFixHighOutlined fontSize="small" />}
									disabled={clipboardWizardDisabledReason !== undefined}
									tooltip={clipboardWizardDisabledReason}
									onClick={() => setClipboardWizardOpen(true)}
									data-testid="dept-config-clipboard-wizard-open"
								>
									{t("department.config.clipboard.wizard.open")}
								</SystemButton>
							)}
						</Box>
					}
					dataTestId="dept-config-integrations-clipboard-header"
				>
					{/* First of the settings, because it governs everything below it: with this off, none of the
					    Clipboard settings in this section reach the vendor at all. */}
					<AgencyIntegrationToggle source={AgencySource.Clipboard} isDirty={isDirty} />
					{isSupport && !isNewDept && clipboardBlockers.length > 0 && (
						<InlineHint
							tone="warning"
							// The message is a heading plus a <ul>; a list inside the default <p> is invalid nesting.
							contentComponent="div"
							dataTestId="dept-config-clipboard-wizard-blockers"
						>
							<Box component="div">
								<Box component="strong" sx={{ display: "block" }}>
									{t("department.config.clipboard.wizard.blocked.title")}
								</Box>
								<Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
									{clipboardBlockers.map((blocker) => (
										<Box component="li" key={blocker}>
											{blocker}
										</Box>
									))}
								</Box>
							</Box>
						</InlineHint>
					)}
					<SettingRow
						label={t("department.config.integrations.clipboard.separateScheduled.label")}
						description={t("department.config.integrations.clipboard.separateScheduled.description")}
						control={
							<SystemSwitch
								checked={separateClipboardScheduled}
								onChange={(event) => setFormData((prev) => ({ ...prev, combineConsecutiveAgencyShifts: !event.target.checked }))}
								dataTestId="dept-config-integrations-separate-clipboard-scheduled-toggle"
							/>
						}
						dataTestId="dept-config-integrations-separate-clipboard-scheduled-row"
					/>
					{/* The third Clipboard switch: actual punches, independent of enrollment and of the facility
					    mapping. It edits `hrisIntegrations` and rides the normal dirty → Save flow. */}
					<SettingRow
						label={t("department.config.integrations.clipboard.syncActualShifts.label")}
						description={
							agencyScheduledWithoutPunchDays > 0
								? t("department.config.integrations.clipboard.syncActualShifts.blocked", { days: agencyScheduledWithoutPunchDays })
								: t("department.config.integrations.clipboard.syncActualShifts.description")
						}
						control={
							<SystemSwitch
								checked={syncActualShifts}
								// A non-zero scheduled-without-punch window means agency shifts are deliberately kept
								// as scheduled-only for that many days, so same-day actuals would double-count.
								// A deactivated integration disables the control WITHOUT touching the stored value:
								// reactivation has to restore exactly what Ops configured, so the switch must keep
								// showing (and keep storing) that choice while it is unreachable.
								disabled={agencyScheduledWithoutPunchDays > 0 || clipboardInactive}
								tooltip={
									clipboardInactive
										? t("department.config.integrations.agency.blocked.inactive", { agency: agencySourceLabel(AgencySource.Clipboard) })
										: undefined
								}
								onChange={(event) => setSyncActualShifts(event.target.checked)}
								dataTestId="dept-config-integrations-clipboard-sync-actuals-toggle"
							/>
						}
						dataTestId="dept-config-integrations-clipboard-sync-actuals-row"
					/>
					<SettingsSection
						headerPlacement="side"
						icon={<LinkOutlined />}
						title={t("department.config.integrations.clipboard.facilityId.label")}
						description={t("department.config.integrations.clipboard.facilityId.description")}
						layout="stack"
						contentMaxWidth={480}
						dataTestId="dept-config-clipboard-facility-id-section"
					>
						{isSupport ? (
							<SystemValidationTextField
								value={formData.clipboardFacilityId ?? ""}
								onChange={(event) =>
									setFormData((prev) => ({
										...prev,
										clipboardFacilityId: event.target.value || null,
									}))
								}
								placeholder={t("department.config.integrations.clipboard.facilityId.placeholder")}
								formControlSx={{ width: 320 }}
								reserveHeightForHelperText={false}
								injectedProps={{ slotProps: { htmlInput: { "data-testid": "dept-config-clipboard-facility-id" } } }}
								dataTestId="dept-config-clipboard-facility-id-field"
							/>
						) : formData.clipboardFacilityId ? (
							// Read-only for non-support: typing a facility id here opens outbound posting to a live
							// agency, so it is a support action. Rendered as metadata rather than a disabled input so
							// it reads as a system value, not a field someone forgot to enable.
							<ReadOnlyMetadataField
								label={t("department.config.integrations.clipboard.facilityId.label")}
								value={formData.clipboardFacilityId}
								copyable
								dataTestId="dept-config-clipboard-facility-id-readonly"
							/>
						) : (
							<InlineHint tone="neutral" dataTestId="dept-config-clipboard-facility-id-unset">
								{t("department.config.integrations.clipboard.facilityId.readOnly")}
							</InlineHint>
						)}
					</SettingsSection>
				</SettingsSection>

				{/* Nursa — the department's identity in the Nursa marketplace, kept beside Clipboard: both are
				    agency marketplaces wired by a single facility id Ops pastes from the vendor. Nursa also
				    fires webhooks per facility, so the id alone is only half the wiring — the facility must
				    also be on our webhook registration, or the department posts shifts out and never hears
				    back. Saving a new id offers that; the button here is the way back in when the prompt was
				    declined or missed, since a correct-but-unsubscribed facility never re-prompts. */}
				<SettingsSection
					headerPlacement="side"
					icon={<StorefrontOutlined />}
					title={t("department.config.integrations.section.nursa")}
					description={t("department.config.integrations.section.nursa.description")}
					layout="stack"
					contentMaxWidth={980}
					// Ungated, for the same reason as Clipboard's: the chip answers the question, the switch acts on it.
					// Two questions, deliberately two chips-worth of answer: the first is about OUR switch, the
					// readiness chips are about Nursa's side of the wiring — a department can be switched On and
					// still exchange nothing. Same `<Box>`-in-`action` shape Clipboard already uses.
					action={
						<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
							<AgencyIntegrationStatusChip source={AgencySource.Nursa} />
							<NursaReadinessChips facilityId={formData.nursaFacilityId ?? null} isSupport={isSupport} />
						</Box>
					}
					dataTestId="dept-config-integrations-nursa-header"
				>
					{/* First of the settings, because it governs everything below it — including the webhook
					    button, which cannot usefully subscribe a facility whose integration is switched off. */}
					<AgencyIntegrationToggle source={AgencySource.Nursa} isDirty={isDirty} />
					<SettingsSection
						headerPlacement="side"
						icon={<LinkOutlined />}
						title={t("department.config.integrations.nursa.facilityId.label")}
						description={t("department.config.integrations.nursa.facilityId.description")}
						layout="stack"
						contentMaxWidth={480}
						dataTestId="dept-config-nursa-facility-id-section"
					>
						{/* Picked from Nursa's own list, by name — the id is Nursa's, not ours, so an operator
						    reading it off a vendor screen and pasting it was where this integration went wrong.
						    The list only ever decorates the field: it stays free-typeable and never disabled, so
						    a Nursa outage can't turn into a configuration outage. */}
						<NursaFacilityIdField
							facilityId={formData.nursaFacilityId ?? null}
							onFacilityIdChange={(nursaFacilityId) => setFormData((prev) => ({ ...prev, nursaFacilityId }))}
							isSupport={isSupport}
							departmentId={chosenDepartment.department_id}
						/>
					</SettingsSection>
					<SettingRow
						label={t("department.config.integrations.nursa.webhook.label")}
						description={t("department.config.integrations.nursa.webhook.description")}
						control={
							<SystemButton
								buttonVariant="secondary"
								size="small"
								// The server reads the PERSISTED id, so an unsaved edit would subscribe the old one.
								// A deactivated integration is refused server-side too; disabling here says why
								// instead of letting the operator subscribe a facility nothing will listen to.
								disabled={!formData.nursaFacilityId || isDirty || isRegisteringNursaWebhook || nursaInactive}
								tooltip={
									nursaInactive
										? t("department.config.integrations.agency.blocked.inactive", { agency: agencySourceLabel(AgencySource.Nursa) })
										: isDirty
											? t("department.config.integrations.nursa.webhook.saveFirst")
											: undefined
								}
								loading={isRegisteringNursaWebhook}
								onClick={onRegisterNursaWebhook}
								data-testid="dept-config-nursa-webhook-button"
							>
								{t("department.config.integrations.nursa.webhook.action")}
							</SystemButton>
						}
						dataTestId="dept-config-integrations-nursa-webhook-row"
					/>
				</SettingsSection>

				{delayedAgencyPostingEnabled && (
					<SettingsSection
						title={t("agency.config.postDelay.title")}
						description={t("agency.config.postDelay.description")}
						icon={<ScheduleOutlinedIcon />}
						headerPlacement="side"
						divider
						// Single full-width row — the default grid would halve it and wrap the label.
						layout="stack"
						// Keeps the title on one line and wraps the helper onto two.
						contentMaxWidth="none"
						dataTestId="dept-config-integrations-agency-post-delay"
					>
						<SettingRow
							label={t("agency.config.postDelay.label")}
							description={t("agency.config.postDelay.helper")}
							control={
								<SystemValidationTextField
									inputType="number"
									value={String(formData.agencyPostDelayDefaultHours ?? DEFAULT_AGENCY_POST_DELAY_HOURS)}
									onChange={(event) => setFormData((prev) => ({ ...prev, agencyPostDelayDefaultHours: Number(event.target.value) }))}
									injectedProps={{ fullWidth: true }}
									formControlSx={{ width: NUMERIC_FIELD_WIDTH.SETTINGS }}
									inputProps={{ inputProps: { min: MIN_AGENCY_POST_DELAY_HOURS } }}
									dataTestId="dept-config-agency-post-delay-input"
								/>
							}
							dataTestId="dept-config-agency-post-delay-row"
						/>
					</SettingsSection>
				)}
			</Box>
			{clipboardWizardOpen && (
				<ClipboardSetupWizard
					open
					onClose={() => setClipboardWizardOpen(false)}
					actualsOn={syncActualShifts}
					onEnrolled={handleClipboardEnrolled}
					onOpenHrisMapping={handleOpenHrisMapping}
				/>
			)}
		</Box>
	);
};
